import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef, useState } from "react";
import { Animated, Modal, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { colors, fonts, shadows } from "@/lib/theme";

/**
 * Three-step coaching sheet shown when an RN taps "Add Manually" before she has
 * put any medication on this visit. The point is not to block manual entry —
 * it is to show her that photographing the bottles is faster and more accurate
 * than typing, at the one moment she is about to type.
 *
 * The hub owns when this appears (see `handleAddManually` in
 * `app/visit/[id]/medications/index.tsx`); this component owns only the steps.
 *
 * Layout, copy, spacing and colour are a direct transcription of the design
 * package "Medication upload wizard design" → `Photo Upload Wizard.dc.html`.
 */

// eslint-disable-next-line @typescript-eslint/no-require-imports
const STEP_1 = require("@/assets/medication-wizard/01-organize.jpg");
// eslint-disable-next-line @typescript-eslint/no-require-imports
const STEP_2 = require("@/assets/medication-wizard/02-straight-on.jpg");
// eslint-disable-next-line @typescript-eslint/no-require-imports
const STEP_3 = require("@/assets/medication-wizard/03-rotate.jpg");

interface Step {
    image: number;
    title: string;
    body: string;
    tip: string;
    next: string;
}

const STEPS: Step[] = [
    {
        image: STEP_1,
        title: "Line the bottles up on the table",
        body: "Ask the patient for every bottle, box and supplement, then stand them in a row with the labels facing you.",
        tip: "One row, labels forward — no bottle hiding behind another.",
        next: "Next: how to hold the phone"
    },
    {
        image: STEP_2,
        title: "Shoot straight on, a few bottles at a time",
        body: "Hold the phone level with the labels, about a foot back, and capture 3–4 bottles per photo. Take as many photos as you need.",
        tip: "Level with the label beats shooting down from above.",
        next: "Next: rotate and repeat"
    },
    {
        image: STEP_3,
        title: "Rotate the bottles and shoot again",
        body: "Turn each bottle a quarter-turn so the wrapped part of the label shows, then take another pass. That's how we catch dose and frequency.",
        tip: "Two passes per bottle is the sweet spot — front, then side.",
        next: "Done — back to the list"
    }
];

interface Props {
    visible: boolean;
    /** "Open Camera" — hands off to useUploadFlow's camera picker. */
    onOpenCamera: () => void;
    /** "Skip" — the deliberate opt-out, straight to the manual-entry screen. */
    onSkip: () => void;
    /** Backdrop tap, hardware back, and the step-3 "Done" — close and stay on
     *  the medications list. Does NOT navigate to manual entry. */
    onDismiss: () => void;
}

export function MedicationUploadWizard({ visible, onOpenCamera, onSkip, onDismiss }: Props) {
    const [step, setStep] = useState(1);
    // Cross-fade between step photos rather than a hard swap, matching the
    // design's 220ms opacity transition. All three are bundled, so there is no
    // load flash between steps.
    const fade = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        if (visible) {
            setStep(1);
            fade.setValue(1);
        }
    }, [visible, fade]);

    const goTo = (nextStep: number) => {
        Animated.sequence([
            Animated.timing(fade, { toValue: 0, duration: 110, useNativeDriver: true }),
            Animated.timing(fade, { toValue: 1, duration: 220, useNativeDriver: true })
        ]).start();
        setStep(nextStep);
    };

    const current = STEPS[Math.min(Math.max(step - 1, 0), 2)];

    const dotColor = (n: number) =>
        step === n ? colors.teal400 : step > n ? colors.teal100 : "#EDEAF2";

    return (
        <Modal
            visible={visible}
            transparent
            animationType="slide"
            onRequestClose={onDismiss}
            statusBarTranslucent
        >
            <View style={ss.scrim}>
                {/* Tapping the dimmed area behind the sheet dismisses, like the
                    design's overlay. */}
                <TouchableOpacity style={ss.scrimFill} activeOpacity={1} onPress={onDismiss} />

                <View style={ss.sheet}>
                    {/* ── Header: Back · progress dots · Skip ── */}
                    <View style={ss.header}>
                        <TouchableOpacity
                            style={ss.headerSide}
                            onPress={() => goTo(step - 1)}
                            disabled={step === 1}
                            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                            activeOpacity={0.6}
                        >
                            <View style={[ss.backRow, step === 1 && ss.hidden]}>
                                <MaterialCommunityIcons
                                    name="chevron-left"
                                    size={20}
                                    color={colors.teal500}
                                />
                                <Text style={ss.backText}>Back</Text>
                            </View>
                        </TouchableOpacity>

                        <View style={ss.dots}>
                            <View style={[ss.dot, { backgroundColor: dotColor(1) }]} />
                            <View style={[ss.dot, { backgroundColor: dotColor(2) }]} />
                            <View style={[ss.dot, { backgroundColor: dotColor(3) }]} />
                        </View>

                        <TouchableOpacity
                            style={[ss.headerSide, ss.headerSideEnd]}
                            onPress={onSkip}
                            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                            activeOpacity={0.6}
                        >
                            <Text style={ss.skipText}>Skip</Text>
                        </TouchableOpacity>
                    </View>

                    {/* ── Headline + proof line ── */}
                    <View style={ss.headlineWrap}>
                        <Text style={ss.headline}>Consider uploading the meds instead</Text>
                        <View style={ss.proofRow}>
                            <View style={ss.proofPill}>
                                <Text style={ss.proofPillText}>~30 SEC</Text>
                            </View>
                            <Text style={ss.proofText}>and far more accurate than typing</Text>
                        </View>
                    </View>

                    {/* ── Step photo + STEP n OF 3 badge ── */}
                    <View style={ss.body}>
                        <View style={ss.photoWrap}>
                            <Animated.Image
                                source={current.image}
                                style={[ss.photo, { opacity: fade }]}
                                resizeMode="cover"
                            />
                            <View style={ss.stepBadge}>
                                <Text style={ss.stepBadgeText}>STEP {step} OF 3</Text>
                            </View>
                        </View>

                        <Text style={ss.stepTitle}>{current.title}</Text>
                        <Text style={ss.stepBody}>{current.body}</Text>

                        <View style={ss.tip}>
                            <MaterialCommunityIcons
                                name="lightbulb-on-outline"
                                size={16}
                                color={colors.teal600}
                            />
                            <Text style={ss.tipText}>{current.tip}</Text>
                        </View>

                        {/* ── Primary action, present on every step ── */}
                        <TouchableOpacity onPress={onOpenCamera} activeOpacity={0.85}>
                            <LinearGradient
                                colors={[colors.teal400, colors.teal500]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                                style={ss.cameraBtn}
                            >
                                <MaterialCommunityIcons name="camera" size={24} color="#FFFFFF" />
                                <Text style={ss.cameraBtnText}>Open Camera</Text>
                            </LinearGradient>
                        </TouchableOpacity>

                        {/* Step 3 ends the wizard and returns to the medications
                            list — which is what its label says. It must NOT fall
                            through to manual entry: sitting through all three
                            steps would then land the nurse exactly where the
                            wizard was talking her out of going. "Skip" in the
                            header is the deliberate opt-out to typing; this is
                            just "I'm done reading". */}
                        <TouchableOpacity
                            style={ss.nextRow}
                            onPress={() => (step >= 3 ? onDismiss() : goTo(step + 1))}
                            activeOpacity={0.6}
                        >
                            <Text style={ss.nextText}>{current.next}</Text>
                            <MaterialCommunityIcons
                                name="chevron-right"
                                size={16}
                                color="#8B89A8"
                            />
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const ss = StyleSheet.create({
    scrim: {
        flex: 1,
        backgroundColor: "rgba(11,11,31,0.55)",
        justifyContent: "flex-end"
    },
    scrimFill: {
        ...StyleSheet.absoluteFill
    },
    sheet: {
        backgroundColor: colors.surface,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        paddingBottom: 30,
        maxHeight: "96%",
        overflow: "hidden"
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingTop: 14,
        paddingHorizontal: 18,
        paddingBottom: 10
    },
    headerSide: {
        minWidth: 64
    },
    headerSideEnd: {
        alignItems: "flex-end"
    },
    backRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 2
    },
    hidden: {
        opacity: 0
    },
    backText: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: colors.teal500
    },
    dots: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6
    },
    dot: {
        width: 26,
        height: 5,
        borderRadius: 9999
    },
    skipText: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: "#8B89A8"
    },
    headlineWrap: {
        paddingHorizontal: 18,
        paddingBottom: 2
    },
    headline: {
        fontSize: 21,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        lineHeight: 27,
        letterSpacing: -0.3
    },
    proofRow: {
        marginTop: 5,
        flexDirection: "row",
        alignItems: "center",
        gap: 6
    },
    proofPill: {
        paddingVertical: 3,
        paddingHorizontal: 9,
        borderRadius: 9999,
        backgroundColor: colors.teal50,
        borderWidth: 1,
        borderColor: colors.teal100
    },
    proofPillText: {
        fontSize: 11,
        fontFamily: fonts.extraBold,
        color: colors.teal600
    },
    proofText: {
        fontSize: 12,
        fontFamily: fonts.semiBold,
        color: colors.textSecondary
    },
    body: {
        paddingHorizontal: 18,
        paddingTop: 12
    },
    photoWrap: {
        // The design's 214pt band, kept. The supplied photos are full-frame
        // 9:16; shown uncropped at this width they are ~650pt tall on an 874pt
        // screen, which pushed the tip strip and the Open Camera CTA off the
        // bottom of the sheet (verified on the iPhone 17 Pro simulator). The
        // assets are therefore pre-framed to this band, each around its own
        // subject rather than a single centre crop.
        height: 214,
        borderRadius: 20,
        overflow: "hidden",
        backgroundColor: "#F5F4F8"
    },
    photo: {
        ...StyleSheet.absoluteFill,
        width: "100%",
        height: "100%"
    },
    stepBadge: {
        position: "absolute",
        top: 12,
        left: 12,
        paddingVertical: 5,
        paddingHorizontal: 11,
        borderRadius: 9999,
        backgroundColor: "rgba(11,11,31,0.62)"
    },
    stepBadgeText: {
        fontSize: 11,
        fontFamily: fonts.extraBold,
        color: "#FFFFFF",
        letterSpacing: 0.4
    },
    stepTitle: {
        marginTop: 16,
        fontSize: 20,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        lineHeight: 26,
        letterSpacing: -0.2
    },
    stepBody: {
        marginTop: 6,
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        lineHeight: 21
    },
    tip: {
        marginTop: 12,
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 8,
        paddingVertical: 11,
        paddingHorizontal: 13,
        borderRadius: 14,
        backgroundColor: colors.teal50,
        borderWidth: 1,
        borderColor: colors.teal100
    },
    tipText: {
        flex: 1,
        fontSize: 12,
        fontFamily: fonts.semiBold,
        color: colors.teal600,
        lineHeight: 18
    },
    cameraBtn: {
        marginTop: 18,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        paddingVertical: 17,
        borderRadius: 9999,
        ...shadows.brand
    },
    cameraBtnText: {
        fontSize: 17,
        fontFamily: fonts.extraBold,
        color: "#FFFFFF"
    },
    nextRow: {
        marginTop: 8,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 5,
        paddingVertical: 11
    },
    nextText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: "#8B89A8"
    }
});
